/**
 * Intelligence Import Manager
 * Admin page for importing program intelligence from public data sources
 * Supports CMS, HRSA, state filings, manual entry, and file uploads
 * Trust Marketplace - Program Intelligence System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Database,
  Upload,
  Play,
  RefreshCw,
  Settings,
  FileText,
  Building2,
  Link as LinkIcon,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Download,
  Calendar,
  Filter,
  Plus,
  Trash2,
  Eye,
  Globe,
  Code,
  Rss,
  Timer,
  Pause,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import StagingPipelineStatus from './StagingPipelineStatus';
import StagingReviewQueue from './StagingReviewQueue';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
interface DataSource {
  id: number;
  source_name: string;
  source_type: string;
  source_url: string;
  source_config: any;
  default_sponsor_type: string;
  default_state: string;
  is_active: boolean;
  last_refresh_at: string;
  avg_data_quality_score: number;
  priority: number;
  // Scheduling fields
  schedule_enabled: boolean;
  schedule_cron: string;
  next_run_at: string;
  last_run_at: string;
  last_run_status: string;
  consecutive_failures: number;
  // Connector metadata
  connector_type: string;
  supports_incremental: boolean;
  supported_filters: string[];
  description: string;
  // Computed stats
  total_jobs?: number;
  completed_jobs?: number;
  total_records_imported?: number;
}

interface ImportJob {
  id: number;
  source_id: number;
  source_name: string;
  job_type: string;
  job_name: string;
  status: string;
  started_at: string;
  completed_at: string;
  total_records: number;
  processed_records: number;
  imported_records: number;
  failed_records: number;
  progress_percentage: number;
  error_message: string;
  created_at: string;
}

interface DiscoveredSponsor {
  id: number;
  name: string;
  organization_type: string;
  organization_type_name: string;
  headquarters_state: string;
  website: string;
  occurrence_count: number;
  status: string;
  linked_sponsor_id: number;
  program_count: number;
  created_at: string;
}

interface Analytics {
  active_sources: number;
  total_jobs: number;
  completed_jobs: number;
  total_imported: number;
  discovered_sponsors: number;
  linked_sponsors: number;
  total_programs: number;
  active_schedules: number;
  pending_review?: number;
}

const IntelligenceImportManager: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Default to 'jobs' tab to show import history first
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [sources, setSources] = useState<DataSource[]>([]);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [sponsors, setSponsors] = useState<DiscoveredSponsor[]>([]);

  // Filters
  const [jobStatusFilter, setJobStatusFilter] = useState<string>('');
  const [sponsorStatusFilter, setSponsorStatusFilter] = useState<string>('');

  // Modals
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);

  // New job form
  const [selectedSourceId, setSelectedSourceId] = useState<number>(0);
  const [jobType, setJobType] = useState('FULL_SYNC');
  const [filterStates, setFilterStates] = useState<string>('');

  // Manual entry form
  const [manualEntry, setManualEntry] = useState({
    program_name: '',
    sponsor_name: '',
    sponsor_type: '',
    program_type: '',
    description: '',
    service_states: '',
    target_enrollment: '',
    application_deadline: ''
  });

  // File upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Delete confirmation
  const [deleteConfirmJob, setDeleteConfirmJob] = useState<number | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteDays, setBulkDeleteDays] = useState(30);
  const [bulkDeleteStatus, setBulkDeleteStatus] = useState<string>('');
  const [bulkDeletePreview, setBulkDeletePreview] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsRes, sourcesRes, jobsRes, sponsorsRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/intelligence-import/analytics/summary')),
        authFetch(getWeaveinApiUrl('/api/intelligence-import/sources')),
        authFetch(getWeaveinApiUrl('/api/intelligence-import/jobs?limit=50')),
        authFetch(getWeaveinApiUrl('/api/intelligence-import/sponsors?limit=100'))
      ]);

      if (!analyticsRes.ok || !sourcesRes.ok || !jobsRes.ok || !sponsorsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [analyticsData, sourcesData, jobsData, sponsorsData] = await Promise.all([
        analyticsRes.json(),
        sourcesRes.json(),
        jobsRes.json(),
        sponsorsRes.json()
      ]);

      setAnalytics(analyticsData);
      setSources(sourcesData);
      setJobs(jobsData);
      setSponsors(sponsorsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const createImportJob = async () => {
    if (!selectedSourceId) {
      alert('Please select a data source');
      return;
    }

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/intelligence-import/jobs'), {
        method: 'POST',
        jsonBody: {
          source_id: selectedSourceId,
          job_type: jobType,
          filter_states: filterStates ? filterStates.split(',').map(s => s.trim()) : []
        }
      });

      if (!response.ok) throw new Error('Failed to create job');

      setShowNewJobModal(false);
      setSelectedSourceId(0);
      fetchData();
      alert('Import job created successfully');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create job');
    }
  };

  const startJob = async (jobId: number) => {
    try {
      await authFetch(getWeaveinApiUrl(`/api/intelligence-import/jobs/${jobId}/start`), {
        method: 'POST'
      });
      fetchData();
    } catch (err) {
      alert('Failed to start job');
    }
  };

  const deleteJob = async (jobId: number) => {
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/intelligence-import/jobs/${jobId}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete job');
      }

      setDeleteConfirmJob(null);
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete job');
    }
  };

  const previewBulkDelete = async () => {
    try {
      const params = new URLSearchParams({
        older_than_days: bulkDeleteDays.toString(),
        dry_run: 'true'
      });
      if (bulkDeleteStatus) {
        params.append('status', bulkDeleteStatus);
      }

      const response = await authFetch(getWeaveinApiUrl(`/api/intelligence-import/jobs?${params.toString()}`), {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to preview delete');

      const result = await response.json();
      setBulkDeletePreview(result);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to preview delete');
    }
  };

  const executeBulkDelete = async () => {
    try {
      const params = new URLSearchParams({
        older_than_days: bulkDeleteDays.toString(),
        dry_run: 'false'
      });
      if (bulkDeleteStatus) {
        params.append('status', bulkDeleteStatus);
      }

      const response = await authFetch(getWeaveinApiUrl(`/api/intelligence-import/jobs?${params.toString()}`), {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete jobs');

      const result = await response.json();
      alert(`Deleted ${result.deleted_count} jobs, ${result.staging_records_deleted} staging records`);
      setShowBulkDeleteModal(false);
      setBulkDeletePreview(null);
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete jobs');
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await authFetch(getWeaveinApiUrl('/api/intelligence-import/upload'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();

      // Process the upload
      await authFetch(getWeaveinApiUrl(`/api/intelligence-import/uploads/${result.upload_id}/process`), {
        method: 'POST'
      });

      setShowUploadModal(false);
      setSelectedFile(null);
      fetchData();
      alert('File uploaded and processing started');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitManualEntry = async () => {
    if (!manualEntry.program_name || !manualEntry.sponsor_name) {
      alert('Program name and sponsor name are required');
      return;
    }

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/intelligence-import/manual'), {
        method: 'POST',
        jsonBody: [{
          program_name: manualEntry.program_name,
          sponsor_name: manualEntry.sponsor_name,
          sponsor_type: manualEntry.sponsor_type || null,
          program_type: manualEntry.program_type || null,
          description: manualEntry.description || null,
          service_states: manualEntry.service_states ? manualEntry.service_states.split(',').map(s => s.trim()) : [],
          target_enrollment: manualEntry.target_enrollment ? parseInt(manualEntry.target_enrollment) : null,
          application_deadline: manualEntry.application_deadline || null
        }]
      });

      if (!response.ok) throw new Error('Import failed');

      setShowManualEntryModal(false);
      setManualEntry({
        program_name: '',
        sponsor_name: '',
        sponsor_type: '',
        program_type: '',
        description: '',
        service_states: '',
        target_enrollment: '',
        application_deadline: ''
      });
      fetchData();
      alert('Program added successfully');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    const colors: Record<string, string> = {
      'PENDING': 'bg-gray-100 text-gray-800',
      'RUNNING': 'bg-blue-100 text-blue-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'FAILED': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-yellow-100 text-yellow-800',
      'DISCOVERED': 'bg-purple-100 text-purple-800',
      'VERIFIED': 'bg-blue-100 text-blue-800',
      'LINKED': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return fmt.dateTime(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
        <span className="ml-2 text-gray-500">{t('admin.loading_import_manager', 'Loading import manager...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <Button onClick={fetchData} className="mt-4">{t('admin.retry', 'Retry')}</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <Database className="h-8 w-8 text-purple-600" />
            {t('admin.intelligence_import_manager', 'Intelligence Import Manager')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('admin.import_program_data_from_cms', 'Import program data from CMS, HRSA, state filings, and other sources')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="h-4 w-4 mr-2" />
            {t('admin.upload_file', 'Upload File')}
          </Button>
          <Button variant="outline" onClick={() => setShowManualEntryModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.manual_entry', 'Manual Entry')}
          </Button>
          <Button onClick={() => setShowNewJobModal(true)}>
            <Play className="h-4 w-4 mr-2" />
            {t('admin.new_import', 'New Import')}
          </Button>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-gray-500">{t('admin.data_sources', 'Data Sources')}</div>
            <div className="text-2xl font-bold text-gray-900">{analytics.active_sources}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-500">{t('admin.total_imported', 'Total Imported')}</div>
            <div className="text-2xl font-bold text-green-600">
              {analytics.total_imported?.toLocaleString() || 0}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-500">{t('admin.discovered_sponsors', 'Discovered Sponsors')}</div>
            <div className="text-2xl font-bold text-purple-600">{analytics.discovered_sponsors}</div>
            <div className="text-xs text-gray-500">{analytics.linked_sponsors} linked</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-500">{t('admin.import_jobs', 'Import Jobs')}</div>
            <div className="text-2xl font-bold text-blue-600">{analytics.completed_jobs}</div>
            <div className="text-xs text-gray-500">of {analytics.total_jobs} total</div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Program import = Sources + Review + Discovered Sponsors. Import-job
            history lives in the ETL Pipeline tab; scheduling in the ETL
            Scheduler — no duplicate scheduler/jobs here. */}
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex gap-1">
          <TabsTrigger value="overview" className="px-4 py-2">{t('admin.data_sources_1', 'Data Sources')}</TabsTrigger>
          <TabsTrigger value="staging" className="px-4 py-2 flex items-center gap-1">
            {t('admin.staging_review', 'Staging Review')}
            {analytics && analytics.pending_review > 0 && (
              <Badge className="bg-yellow-500 text-white text-xs ml-1">{analytics.pending_review}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sponsors" className="px-4 py-2">{t('admin.discovered_sponsors_1', 'Discovered Sponsors')}</TabsTrigger>
        </TabsList>

        {/* Data Sources Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('admin.configured_data_sources', 'Configured Data Sources')}</h3>
              <div className="text-sm text-gray-500">
                {sources.filter(s => s.schedule_enabled).length} scheduled / {sources.length} total
              </div>
            </div>
            <div className="space-y-4">
              {sources.map(source => (
                <div key={source.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Connector Type Icon */}
                      {source.connector_type === 'api' && <Globe className="h-5 w-5 text-blue-500" />}
                      {source.connector_type === 'scrape' && <Code className="h-5 w-5 text-purple-500" />}
                      {source.connector_type === 'manual' && <FileText className="h-5 w-5 text-gray-500" />}
                      {!source.connector_type && <Database className="h-5 w-5 text-gray-400" />}

                      <span className="font-semibold text-lg">{source.source_name}</span>

                      {/* Type Badges */}
                      <Badge variant="outline" className="text-xs">
                        {source.connector_type || source.source_type}
                      </Badge>

                      {source.supports_incremental && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{t('admin.incremental', 'Incremental')}</Badge>
                      )}

                      {/* Status Badges */}
                      {source.is_active ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">{t('admin.active', 'Active')}</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800 text-xs">{t('admin.inactive', 'Inactive')}</Badge>
                      )}

                      {/* Schedule Status */}
                      {source.schedule_enabled ? (
                        <Badge className="bg-purple-100 text-purple-800 text-xs flex items-center gap-1">
                          <Timer className="h-3 w-3" />
                          {t('admin.scheduled', 'Scheduled')}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 text-xs">{t('admin.manual', 'Manual')}</Badge>
                      )}

                      {/* Failure Warning */}
                      {source.consecutive_failures > 0 && (
                        <Badge className="bg-red-100 text-red-800 text-xs flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {source.consecutive_failures} failures
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSourceId(source.id);
                          setShowNewJobModal(true);
                        }}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        {t('admin.import', 'Import')}
                      </Button>
                    </div>
                  </div>

                  {/* Description */}
                  {source.description && (
                    <p className="text-sm text-gray-600 mb-3">{source.description}</p>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide">{t('admin.sponsor_type', 'Sponsor Type')}</span>
                      <span className="font-medium">{source.default_sponsor_type || 'Any'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide">{t('admin.state', 'State')}</span>
                      <span className="font-medium">{source.default_state || 'All'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide">{t('admin.priority', 'Priority')}</span>
                      <span className="font-medium">{source.priority}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide">{t('admin.last_run', 'Last Run')}</span>
                      <span className="font-medium">
                        {source.last_run_at ? formatDate(source.last_run_at) : 'Never'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-xs uppercase tracking-wide">{t('admin.total_imported_1', 'Total Imported')}</span>
                      <span className="font-medium text-green-600">
                        {source.total_records_imported?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>

                  {/* Schedule Info */}
                  {source.schedule_enabled && source.schedule_cron && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-purple-600">
                        <Timer className="h-4 w-4" />
                        <span className="font-mono text-xs">{source.schedule_cron}</span>
                      </div>
                      {source.next_run_at && (
                        <div className="text-gray-500">
                          Next: {formatDate(source.next_run_at)}
                        </div>
                      )}
                      {source.last_run_status && (
                        <div className={`flex items-center gap-1 ${
                          source.last_run_status === 'COMPLETED' ? 'text-green-600' :
                          source.last_run_status === 'FAILED' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {source.last_run_status === 'COMPLETED' && <CheckCircle2 className="h-3 w-3" />}
                          {source.last_run_status === 'FAILED' && <XCircle className="h-3 w-3" />}
                          {source.last_run_status}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Filters */}
                  {source.supported_filters && source.supported_filters.length > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-500">{t('admin.filters', 'Filters:')}</span>
                      {source.supported_filters.map(filter => (
                        <Badge key={filter} variant="outline" className="text-xs">
                          {filter}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Staging Review Tab */}
        <TabsContent value="staging" className="mt-4">
          <StagingReviewQueue
            apiBaseUrl={getWeaveinApiUrl('/api/intelligence-import')}
            authToken={localStorage.getItem('auth_token') || undefined}
            onRefresh={fetchData}
          />
        </TabsContent>

        {/* Discovered Sponsors Tab */}
        <TabsContent value="sponsors" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('admin.discovered_sponsors_2', 'Discovered Sponsors')}</h3>
              <select
                value={sponsorStatusFilter}
                onChange={(e) => setSponsorStatusFilter(e.target.value)}
                className="px-3 py-1 border rounded-lg text-sm"
              >
                <option value="">{t('admin.all_status_1', 'All Status')}</option>
                <option value="DISCOVERED">{t('admin.discovered', 'Discovered')}</option>
                <option value="VERIFIED">{t('admin.verified', 'Verified')}</option>
                <option value="LINKED">{t('admin.linked', 'Linked')}</option>
              </select>
            </div>
            <div className="space-y-3">
              {sponsors
                .filter(s => !sponsorStatusFilter || s.status === sponsorStatusFilter)
                .map(sponsor => (
                  <div key={sponsor.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{sponsor.name}</span>
                        <Badge className={getStatusBadgeColor(sponsor.status)}>
                          {sponsor.status}
                        </Badge>
                        {sponsor.organization_type_name && (
                          <Badge variant="outline">{sponsor.organization_type_name}</Badge>
                        )}
                      </div>
                      {!sponsor.linked_sponsor_id && (
                        <Button size="sm" variant="outline">
                          <LinkIcon className="h-3 w-3 mr-1" />
                          {t('admin.link', 'Link')}
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">{t('admin.state_1', 'State:')}</span>
                        <span className="ml-1">{sponsor.headquarters_state || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('admin.programs', 'Programs:')}</span>
                        <span className="ml-1">{sponsor.program_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('admin.occurrences', 'Occurrences:')}</span>
                        <span className="ml-1">{sponsor.occurrence_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('admin.discovered_1', 'Discovered:')}</span>
                        <span className="ml-1">{formatDate(sponsor.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        </TabsContent>

      </Tabs>

      {/* New Job Modal */}
      <Dialog open={showNewJobModal} onOpenChange={setShowNewJobModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.create_import_job', 'Create Import Job')}</DialogTitle>
            <DialogDescription>
              {t('admin.select_a_data_source_and', 'Select a data source and configure the import')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.data_source', 'Data Source *')}</label>
              <select
                value={selectedSourceId}
                onChange={(e) => setSelectedSourceId(parseInt(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value={0}>{t('admin.select_a_source', 'Select a source...')}</option>
                {sources.filter(s => s.is_active).map(source => (
                  <option key={source.id} value={source.id}>
                    {source.source_name} ({source.source_type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.job_type', 'Job Type')}</label>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="FULL_SYNC">{t('admin.full_sync', 'Full Sync')}</option>
                <option value="INCREMENTAL">{t('admin.incremental_1', 'Incremental')}</option>
                <option value="PREVIEW">{t('admin.preview_only', 'Preview Only')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Filter States (optional)</label>
              <input
                type="text"
                value={filterStates}
                onChange={(e) => setFilterStates(e.target.value)}
                placeholder="e.g., CA, TX, NY"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowNewJobModal(false)}>
              {t('admin.cancel_1', 'Cancel')}
            </Button>
            <Button onClick={createImportJob}>
              <Play className="h-4 w-4 mr-2" />
              {t('admin.create_job', 'Create Job')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.upload_file_1', 'Upload File')}</DialogTitle>
            <DialogDescription>
              {t('admin.upload_csv_xlsx_or_json', 'Upload CSV, XLSX, or JSON file with program data')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.json"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                {selectedFile ? (
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('admin.click_to_select_or_drag', 'Click to select or drag and drop')}
                  </p>
                )}
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              {t('admin.cancel_2', 'Cancel')}
            </Button>
            <Button onClick={handleFileUpload} disabled={!selectedFile || uploading}>
              {uploading ? <LoadingSpinner className="h-4 w-4" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog open={showManualEntryModal} onOpenChange={setShowManualEntryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin.manual_entry_1', 'Manual Entry')}</DialogTitle>
            <DialogDescription>
              {t('admin.manually_add_a_program_to', 'Manually add a program to the intelligence catalog')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium mb-1">{t('admin.program_name', 'Program Name *')}</label>
              <input
                type="text"
                value={manualEntry.program_name}
                onChange={(e) => setManualEntry({ ...manualEntry, program_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('admin.sponsor_name', 'Sponsor Name *')}</label>
              <input
                type="text"
                value={manualEntry.sponsor_name}
                onChange={(e) => setManualEntry({ ...manualEntry, sponsor_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.sponsor_type_1', 'Sponsor Type')}</label>
                <select
                  value={manualEntry.sponsor_type}
                  onChange={(e) => setManualEntry({ ...manualEntry, sponsor_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">{t('admin.select', 'Select...')}</option>
                  <option value="HEALTH_PLAN">{t('admin.health_plan', 'Health Plan')}</option>
                  <option value="MANAGED_CARE">{t('admin.managed_care', 'Managed Care')}</option>
                  <option value="STATE_AUTHORITY">{t('admin.state_authority', 'State Authority')}</option>
                  <option value="ACO">ACO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.program_type', 'Program Type')}</label>
                <select
                  value={manualEntry.program_type}
                  onChange={(e) => setManualEntry({ ...manualEntry, program_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">{t('admin.select_1', 'Select...')}</option>
                  <option value="CalAIM_ECM">{t('admin.calaim_ecm', 'CalAIM ECM')}</option>
                  <option value="CalAIM_CS">{t('admin.calaim_cs', 'CalAIM CS')}</option>
                  <option value="Housing">{t('admin.housing', 'Housing')}</option>
                  <option value="Food">{t('admin.food', 'Food')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('admin.description', 'Description')}</label>
              <textarea
                value={manualEntry.description}
                onChange={(e) => setManualEntry({ ...manualEntry, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">States (comma separated)</label>
                <input
                  type="text"
                  value={manualEntry.service_states}
                  onChange={(e) => setManualEntry({ ...manualEntry, service_states: e.target.value })}
                  placeholder="CA, TX"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.target_enrollment', 'Target Enrollment')}</label>
                <input
                  type="number"
                  value={manualEntry.target_enrollment}
                  onChange={(e) => setManualEntry({ ...manualEntry, target_enrollment: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('admin.application_deadline', 'Application Deadline')}</label>
              <input
                type="date"
                value={manualEntry.application_deadline}
                onChange={(e) => setManualEntry({ ...manualEntry, application_deadline: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowManualEntryModal(false)}>
              {t('admin.cancel_3', 'Cancel')}
            </Button>
            <Button onClick={submitManualEntry}>
              <Plus className="h-4 w-4 mr-2" />
              {t('admin.add_program', 'Add Program')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Modal */}
      <Dialog open={showBulkDeleteModal} onOpenChange={(open) => {
        setShowBulkDeleteModal(open);
        if (!open) setBulkDeletePreview(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.delete_old_import_jobs', 'Delete Old Import Jobs')}</DialogTitle>
            <DialogDescription>
              {t('admin.delete_jobs_older_than_a', 'Delete jobs older than a specified number of days')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.older_than_days', 'Older than (days)')}</label>
              <input
                type="number"
                value={bulkDeleteDays}
                onChange={(e) => {
                  setBulkDeleteDays(parseInt(e.target.value) || 30);
                  setBulkDeletePreview(null);
                }}
                min={1}
                max={365}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('admin.status_optional', 'Status (optional)')}</label>
              <select
                value={bulkDeleteStatus}
                onChange={(e) => {
                  setBulkDeleteStatus(e.target.value);
                  setBulkDeletePreview(null);
                }}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">{t('admin.all_except_running', 'All (except RUNNING)')}</option>
                <option value="COMPLETED">{t('admin.completed_1', 'Completed')}</option>
                <option value="FAILED">{t('admin.failed_1', 'Failed')}</option>
                <option value="CANCELLED">{t('admin.cancelled', 'Cancelled')}</option>
                <option value="PENDING">{t('admin.pending_1', 'Pending')}</option>
              </select>
            </div>

            {bulkDeletePreview && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium mb-2">
                  Preview: {bulkDeletePreview.job_count} jobs will be deleted
                </div>
                {bulkDeletePreview.jobs && bulkDeletePreview.jobs.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {bulkDeletePreview.jobs.slice(0, 10).map((job: any) => (
                      <div key={job.id} className="flex justify-between text-gray-600">
                        <span>{job.name}</span>
                        <span>{job.status}</span>
                      </div>
                    ))}
                    {bulkDeletePreview.jobs.length > 10 && (
                      <div className="text-gray-500 italic">
                        ... and {bulkDeletePreview.jobs.length - 10} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowBulkDeleteModal(false);
              setBulkDeletePreview(null);
            }}>
              {t('admin.cancel_4', 'Cancel')}
            </Button>
            {!bulkDeletePreview ? (
              <Button variant="outline" onClick={previewBulkDelete}>
                <Eye className="h-4 w-4 mr-2" />
                {t('admin.preview', 'Preview')}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={executeBulkDelete}
                disabled={bulkDeletePreview.job_count === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {bulkDeletePreview.job_count} Jobs
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default IntelligenceImportManager;
